import {
  createOidcUserManager,
  errorPayload,
  tokenExp,
  tokenIat,
  tokenIsExpired
} from '../../utils/oidcHelpers';
import { checkIsAPNICStaffByEmail } from '../../utils/common';
import { oidcSettings } from '../../config/oidc';
import store from '../store';
import router from '../../router';
import requestInstance from '../../utils/requestInstance';

const oidcUserManager = createOidcUserManager(oidcSettings);

const authStore = {
  namespaced: true,

  state: {
    access_token: null,
    id_token: null,
    refresh_token: null,
    user: null,
    scopes: null,
    is_checked: false,
    events_are_bound: false,
    error: null
  },

  getters: {
    oidcIsAuthenticated: state => {
      return !!state.access_token;
    },

    oidcAuthenticationIsChecked: state => {
      return state.is_checked;
    },

    oidcIdTokenExp: state => {
      return tokenExp(state.id_token);
    },

    oidcIdTokenIat: state => {
      return tokenIat(state.id_token);
    },

    oidcUser: state => {
      return state.user;
    },

    oidcIdToken: state => {
      return tokenIsExpired(state.id_token) ? null : state.id_token;
    }
  },

  mutations: {
    unsetOidcAuth(state) {
      state.id_token = null;
      state.access_token = null;
      state.refresh_token = null;
      state.user = null;
    },

    setOidcAuth(state, user) {
      state.id_token = user.id_token;
      state.access_token = user.access_token;
      state.refresh_token = user.refresh_token;
      state.user = user.profile;
      state.scopes = user.scopes;
      state.error = null;
    },

    setOidcError(state, payload) {
      state.error = payload.error;
    },

    setOidcAuthIsChecked(state) {
      state.is_checked = true;
    },

    setOidcEventsAreBound(state) {
      state.events_are_bound = true;
    }
  },

  actions: {
    authenticateOidc(context) {
      return oidcUserManager.signinRedirect().catch(err => {
        context.commit('setOidcError', errorPayload('authenticateOidc', err));
      });
    },

    signOutOidc(context) {
      return oidcUserManager.signoutRedirect().then(() => {
        context.commit('unsetOidcAuth');
      });
    },

    oidcSignInCallback(context, url) {
      return new Promise((resolve, reject) => {
        oidcUserManager
          .signinRedirectCallback(url)
          .then(user => {
            context.dispatch('oidcWasAuthenticated', user);
            resolve('/');
          })
          .catch(err => {
            context.commit(
              'setOidcError',
              errorPayload('oidcSignInCallback', err)
            );
            context.commit('setOidcAuthIsChecked');
            reject(err);
          });
      });
    },

    oidcWasAuthenticated(context, user) {
      context.commit('setOidcAuth', user);
      if (!context.state.events_are_bound) {
        oidcUserManager.events.addAccessTokenExpired(() => {
          store.state.tokenExpiredHandlerTimer = setTimeout(() => {
            store.dispatch('authStore/sessionExpiryLogout');
            store.state.tokenExpiredHandlerTimer = null;
          }, 3000);

          context.commit('unsetOidcAuth');
        });
        if (oidcSettings.automaticSilentRenew) {
          oidcUserManager.events.addAccessTokenExpiring(() => {
            authenticateOidcSilent(context).catch(err => {
              console.error('authenticateOidcSilent error: ', err);
            });
          });
        }
        context.commit('setOidcEventsAreBound');
      }
      context.commit('setOidcAuthIsChecked');
    },

    authenticateOidcSilent(context) {
      return authenticateOidcSilent(context);
    },

    silentLogOut(context) {
      // sometimes when user login, a user signed out event has been triggered,
      // this is used to prevent user logout instantly
      // so, if the function is been triggered within 10 seconds of user login event
      // ignore this event
      if (new Date().getTime() < context.getters.oidcIdTokenIat + 10000) return;

      context.commit('unsetOidcAuth');
    },

    sessionExpiryLogout(context) {
      const closeSessionExpiredModalAt = sessionStorage.getItem(
        'close_session_expired_modal_at'
      );
      const minIntervalBetweenSessionExpiredModals = 60000;

      if (
        closeSessionExpiredModalAt &&
        new Date().getTime() - closeSessionExpiredModalAt <
          minIntervalBetweenSessionExpiredModals
      )
        return;

      const path =
        router.history && router.history.current && router.history.current.path;

      if (path === '/' || path === '/welcome') return;

      context.commit('unsetOidcAuth');
      store.commit('setIsShowSessionExpiredModal', true);
    }
  }
};

const authenticateOidcSilent = context => {
  return new Promise((resolve, reject) => {
    oidcUserManager
      .signinSilent()
      .then(user => {
        context.dispatch('oidcWasAuthenticated', user);
        resolve(user);
      })
      .catch(err => {
        context.commit('setOidcAuthIsChecked');
        context.commit(
          'setOidcError',
          errorPayload('authenticateOidcSilent', err)
        );
        reject(err);
      });
  });
};

oidcUserManager.events.addUserLoaded(user => {
  requestInstance.defaults.headers.common['Authorization'] =
    'Bearer ' + user.access_token;

  if (store.state.tokenExpiredHandlerTimer) {
    clearTimeout(store.state.tokenExpiredHandlerTimer);
    store.state.tokenExpiredHandlerTimer = null;
  }

  const userEmail = user?.profile?.email;
  const isAPNICStaff = checkIsAPNICStaffByEmail(userEmail);

  if (isAPNICStaff) store.commit('setIsAPNICStaff', true);

  if (!store.getters['dataStore/dataReady'])
    store.dispatch('dataStore/fetchMetaData');
});

oidcUserManager.events.addAccessTokenExpiring(() => {
  store.dispatch('authStore/authenticateOidcSilent');
});

oidcUserManager.events.addUserSignedOut(() => {
  store.dispatch('authStore/silentLogOut');
});

export default authStore;
